import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui-kit/button';
import { CheckCircle, FileText, Users, Settings } from 'lucide-react';
import type { StepCompleteProps } from '../types/onboarding.types';

export function StepComplete({ orgName, orgSlug, onComplete, isLoading }: StepCompleteProps) {
  const { t } = useTranslation();

  const quickLinks = [
    {
      icon: FileText,
      title: t('ONBOARDING_COMPLETE_BROWSETEMPLATES'),
      description: t('ONBOARDING_COMPLETE_BROWSETEMPLATESDESCRIPTION'),
      href: orgSlug ? `/org/${orgSlug}/templates` : '/org/new',
    },
    ...(orgSlug
      ? [
        {
          icon: Users,
          title: t('ONBOARDING_COMPLETE_MANAGETEAM'),
          description: t('ONBOARDING_COMPLETE_MANAGETEAMDESCRIPTION'),
          href: `/org/${orgSlug}/users`,
        },
        {
          icon: Settings,
          title: t('ONBOARDING_COMPLETE_SETTINGS'),
          description: t('ONBOARDING_COMPLETE_SETTINGSDESCRIPTION'),
          href: `/org/${orgSlug}/settings`,
        },
      ]
      : []),
  ];

  return (
    <div className="space-y-8">
      <div className="text-center">
        <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-green-500/20">
          <CheckCircle className="h-10 w-10 text-green-500" />
        </div>
        <h2 className="mt-6 text-2xl font-bold text-foreground">
          {t('ONBOARDING_COMPLETE_TITLE')}
        </h2>
        <p className="mt-2 text-muted-foreground">
          {orgName
            ? t('ONBOARDING_COMPLETE_DESCRIPTION', { orgName })
            : t('ONBOARDING_COMPLETE_DESCRIPTIONNOORG')}
        </p>
      </div>

      {quickLinks.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-medium text-muted-foreground">
            {t('ONBOARDING_COMPLETE_QUICKLINKS')}
          </h3>
          <div className="grid gap-3 sm:grid-cols-3">
            {quickLinks.map((link) => (
              <Link
                key={link.title}
                to={link.href}
                className="group flex flex-col rounded-lg border bg-background p-4 transition-all hover:border-primary/30 hover:shadow-sm"
              >
                <link.icon className="h-5 w-5 text-primary" />
                <span className="mt-2 font-medium text-foreground group-hover:text-primary">
                  {link.title}
                </span>
                <span className="mt-0.5 text-xs text-muted-foreground">{link.description}</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="flex justify-center pt-4">
        <Button onClick={onComplete} disabled={isLoading} size="lg" className="min-w-50">
          {isLoading
            ? t('ONBOARDING_BUTTONS_LOADING')
            : t('ONBOARDING_BUTTONS_GOTODASHBOARD')}
        </Button>
      </div>
    </div>
  );
}
